import React from 'react'
import { Button } from 'antd'

interface TranslationQualitySectionProps {
  isAdvanced: boolean
  isMultiTargetMode: boolean
  quickSetupSummary?: React.ReactNode
  quickSetupNotice?: React.ReactNode
  description?: React.ReactNode
  canAdjustSettings?: boolean
  onAdjustSettings: () => void
  onBackToQuickSetup: () => void
  advancedDisabledText?: React.ReactNode
  modelControl?: React.ReactNode
  quotationControl?: React.ReactNode
  proofreadControl?: React.ReactNode
  characterAnalysisControl?: React.ReactNode
  frenchOptions?: React.ReactNode
  queuedTranslationControl?: React.ReactNode
}

const TranslationQualitySection: React.FC<TranslationQualitySectionProps> = ({
  isAdvanced,
  isMultiTargetMode,
  quickSetupSummary,
  quickSetupNotice,
  description,
  canAdjustSettings = true,
  onAdjustSettings,
  onBackToQuickSetup,
  advancedDisabledText,
  modelControl,
  quotationControl,
  proofreadControl,
  characterAnalysisControl,
  frenchOptions,
  queuedTranslationControl,
}) => {
  const hasAdvancedContent = Boolean(
    modelControl ||
    quotationControl ||
    frenchOptions ||
    proofreadControl ||
    characterAnalysisControl ||
    queuedTranslationControl
  )
  const isEmptyState = !quickSetupSummary && !quickSetupNotice && !advancedDisabledText && !hasAdvancedContent

  return (
    <section
      className={`translation-modal-block${isEmptyState ? ' translation-modal-block--quality-empty' : ''}`}
      aria-labelledby="translation-quality-heading"
      data-testid="tutorial:translation:quality-section"
    >
      <div className="translation-modal-block-header">
        <div>
          <h3 id="translation-quality-heading">Quality and behavior</h3>
          <p>{description || 'Review style, quality, and language behavior for this translation.'}</p>
        </div>
        {!isMultiTargetMode && canAdjustSettings ? (
          <Button type="link" onClick={isAdvanced ? onBackToQuickSetup : onAdjustSettings}>
            {isAdvanced ? 'Back to quick setup' : 'Adjust settings'}
          </Button>
        ) : null}
      </div>
      {advancedDisabledText ? (
        <div className="translation-quality-helper">{advancedDisabledText}</div>
      ) : null}
      {!isAdvanced && quickSetupSummary ? (
        <div className="translation-quality-conversation-summary" data-testid="tutorial:translation:quick-setup-summary">{quickSetupSummary}</div>
      ) : null}
      {!isAdvanced && quickSetupNotice ? quickSetupNotice : null}
      {isAdvanced && hasAdvancedContent ? (
        <div className="translation-advanced-controls" data-testid="tutorial:translation:advanced-settings">
          <div className="translation-modal-subheading">Advanced settings</div>
          <p className="translation-advanced-helper">Most authors can leave these settings unchanged.</p>
          {modelControl || quotationControl ? (
            <div className="translation-quality-grid">
              {modelControl}
              {quotationControl}
            </div>
          ) : null}
          {frenchOptions}
          {proofreadControl || characterAnalysisControl || queuedTranslationControl ? (
            <div className="translation-quality-grid">
              {proofreadControl}
              {characterAnalysisControl}
              {queuedTranslationControl}
            </div>
          ) : null}
        </div>
      ) : null}
    </section>
  )
}

export default TranslationQualitySection
